import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const EXPAND_SETTLE_MS_DEFAULT = 360;

function isClosingPhase(phase) {
  return String(phase || '').startsWith('close');
}

export default function AnimatedTerminalShell({
  isOpen,
  onOpenChange,
  buttonLabel = 'TERMINAL',
  buttonTitleOpen = 'Skjul terminal',
  buttonTitleClosed = 'Vis terminal',
  buttonAriaOpen = 'Skjul terminal',
  buttonAriaClosed = 'Vis terminal',
  buttonStyle = {},
  panelStyle = {},
  collapsedPanelStyle = {},
  baseWidth = 520,
  baseHeight = 360,
  expandScale = 2,
  expandSettleMs = EXPAND_SETTLE_MS_DEFAULT,
  panelZIndex = 14,
  buttonZIndex = 13,
  children,
}) {
  const [phase, setPhase] = useState('closed');
  const [isExpanded, setIsExpanded] = useState(false);

  const phaseTimersRef = useRef([]);
  const delayedCloseTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (delayedCloseTimerRef.current != null) {
      window.clearTimeout(delayedCloseTimerRef.current);
      delayedCloseTimerRef.current = null;
    }

    if (phaseTimersRef.current.length) {
      phaseTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      phaseTimersRef.current = [];
    }
  }, []);

  const schedulePhase = useCallback((nextPhase, delayMs) => {
    const timerId = window.setTimeout(() => {
      setPhase(nextPhase);
      phaseTimersRef.current = phaseTimersRef.current.filter((id) => id !== timerId);
    }, delayMs);
    phaseTimersRef.current.push(timerId);
  }, []);

  const startOpenAnimation = useCallback(() => {
    clearTimers();
    setIsExpanded(false);
    setPhase('open-button-glow');
    schedulePhase('open-border-draw', 500);
    schedulePhase('open-bg-white', 1000);
    schedulePhase('open-normalize', 1500);
    schedulePhase('open', 2000);
  }, [clearTimers, schedulePhase]);

  const startCloseAnimation = useCallback(() => {
    clearTimers();
    setPhase('close-button-glow');
    schedulePhase('close-content-fade', 500);
    schedulePhase('close-bg-fade', 1000);
    schedulePhase('close-border-erase', 1500);
    schedulePhase('closed', 2000);
  }, [clearTimers, schedulePhase]);

  const requestClose = useCallback((fromButton = false) => {
    if (typeof onOpenChange === 'function') {
      onOpenChange(false);
    }

    if (fromButton && isExpanded) {
      clearTimers();
      setIsExpanded(false);
      delayedCloseTimerRef.current = window.setTimeout(() => {
        delayedCloseTimerRef.current = null;
        startCloseAnimation();
      }, Math.max(0, Number(expandSettleMs) || EXPAND_SETTLE_MS_DEFAULT));
      return;
    }

    setIsExpanded(false);
    startCloseAnimation();
  }, [clearTimers, expandSettleMs, isExpanded, onOpenChange, startCloseAnimation]);

  const toggleExpand = useCallback(() => {
    if (phase === 'closed' || isClosingPhase(phase)) return;
    setIsExpanded((prev) => !prev);
  }, [phase]);

  useEffect(() => {
    if (isOpen) {
      if (phase === 'closed' || isClosingPhase(phase)) {
        startOpenAnimation();
      }
      return;
    }

    if (phase === 'closed' || isClosingPhase(phase)) return;
    requestClose(false);
  }, [isOpen, phase, requestClose, startOpenAnimation]);

  useEffect(() => () => {
    clearTimers();
  }, [clearTimers]);

  const panelVisible = phase !== 'closed' && phase !== 'open-button-glow';
  const buttonGlow = phase === 'open-button-glow'
    || phase === 'open-border-draw'
    || phase === 'open-bg-white'
    || phase === 'close-button-glow'
    || phase === 'close-content-fade'
    || phase === 'close-bg-fade';
  const buttonActive = panelVisible || buttonGlow;

  const panelGlowBackground = phase === 'open-bg-white'
    || phase === 'close-content-fade'
    || phase === 'close-bg-fade'
    || phase === 'close-border-erase';

  const panelGlowBackgroundOpacity = phase === 'open-border-draw' ? 0 : 1;

  const panelGlowBorder = phase === 'open-border-draw'
    || phase === 'open-bg-white'
    || phase === 'close-content-fade'
    || phase === 'close-bg-fade'
    || phase === 'close-border-erase';

  const panelDrawBorder = phase === 'open-border-draw';
  const panelEraseBorder = phase === 'close-border-erase';
  const panelHideContent = phase === 'open-border-draw'
    || phase === 'open-bg-white'
    || phase === 'close-content-fade'
    || phase === 'close-bg-fade'
    || phase === 'close-border-erase'
    || phase === 'closed';

  const panelMoveBehind = phase === 'close-border-erase';
  const panelOpenFromButtonAnchor = phase === 'open-border-draw';

  const panelShrinkHeight = phase === 'open-border-draw'
    || phase === 'close-border-erase'
    || phase === 'closed';

  const expandedWidth = Math.round(baseWidth * expandScale);
  const expandedHeight = Math.round(baseHeight * expandScale);
  const contentScale = isExpanded ? expandScale : 1;
  const contentInverseScale = 1 / contentScale;

  const panelWidth = isExpanded
    ? `min(${expandedWidth}px, calc(100vw - 48px))`
    : `min(${baseWidth}px, calc(100vw - 48px))`;

  const panelHeight = isExpanded
    ? `min(${expandedHeight}px, calc(100vh - 48px))`
    : `${panelShrinkHeight ? 0 : baseHeight}px`;

  const basePanelOpenPosition = useMemo(() => ({
    ...panelStyle,
  }), [panelStyle]);

  const basePanelCollapsedPosition = useMemo(() => ({
    ...collapsedPanelStyle,
  }), [collapsedPanelStyle]);

  const panelPosition = basePanelOpenPosition.position
    || basePanelCollapsedPosition.position
    || 'relative';

  const activePanelPosition = isExpanded
    ? {
      position: panelPosition,
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    }
    : panelMoveBehind || panelOpenFromButtonAnchor
      ? basePanelCollapsedPosition
      : basePanelOpenPosition;

  return (
    <>
      <style>{`
        @keyframes dse-terminal-shell-border-draw {
          0% {
            stroke-dashoffset: 1;
            opacity: 1;
          }
          100% {
            stroke-dashoffset: 0;
            opacity: 1;
          }
        }
        @keyframes dse-terminal-shell-border-erase {
          0% {
            stroke-dashoffset: 0;
            opacity: 1;
          }
          100% {
            stroke-dashoffset: 1;
            opacity: 1;
          }
        }
      `}</style>

      <button
        type="button"
        onClick={() => {
          if (isOpen) {
            requestClose(true);
            return;
          }
          if (typeof onOpenChange === 'function') {
            onOpenChange(true);
          }
        }}
        title={isOpen ? buttonTitleOpen : buttonTitleClosed}
        aria-label={isOpen ? buttonAriaOpen : buttonAriaClosed}
        style={{
          ...buttonStyle,
          border: buttonGlow
            ? '1px solid rgba(255,255,255,0.96)'
            : buttonActive
              ? '1px solid rgba(251,191,36,0.85)'
              : '1px solid rgba(255,255,255,0.42)',
          background: buttonGlow
            ? 'linear-gradient(180deg, rgba(255,255,255,0.99), rgba(245,245,245,0.97))'
            : buttonActive
              ? 'radial-gradient(circle at 30% 20%, rgba(251,191,36,0.45), rgba(34,34,34,0.9) 68%)'
              : 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.18), rgba(0,0,0,0.8) 66%)',
          color: '#ffffff',
          fontWeight: 700,
          boxShadow: buttonGlow
            ? '0 0 28px rgba(255,255,255,0.98), 0 0 88px rgba(255,255,255,0.86)'
            : buttonActive
              ? '0 14px 30px rgba(0,0,0,0.58), inset 0 0 0 1px rgba(251,191,36,0.32)'
              : '0 12px 24px rgba(0,0,0,0.5)',
          transition: 'border-color 500ms ease, background 500ms ease, box-shadow 500ms ease, color 500ms ease',
          zIndex: buttonZIndex,
        }}
      >
        {buttonLabel}
      </button>

      {panelVisible ? (
        <div
          style={{
            ...activePanelPosition,
            position: activePanelPosition.position || panelPosition,
            width: panelWidth,
            height: panelHeight,
            borderRadius: 16,
            border: `1px solid ${panelGlowBorder ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.22)'}`,
            background: 'transparent',
            boxShadow: panelGlowBorder
              ? '0 0 32px rgba(255,255,255,0.95), 0 0 88px rgba(255,255,255,0.72)'
              : '0 18px 40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
            overflow: 'hidden',
            zIndex: panelZIndex,
            transition: `top 500ms ease, left ${Math.max(0, Number(expandSettleMs) || EXPAND_SETTLE_MS_DEFAULT)}ms ease, transform ${Math.max(0, Number(expandSettleMs) || EXPAND_SETTLE_MS_DEFAULT)}ms ease, width 500ms ease, height 500ms ease, background 500ms ease, border-color 500ms ease, box-shadow 500ms ease`,
            pointerEvents: panelShrinkHeight ? 'none' : 'auto',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              pointerEvents: 'none',
              background: panelGlowBackground
                ? 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(245,245,245,0.96))'
                : 'linear-gradient(180deg, rgba(7,7,10,0.95), rgba(3,3,4,0.95))',
              opacity: panelGlowBackgroundOpacity,
              transition: 'background 500ms ease, opacity 500ms ease',
            }}
          />

          {panelDrawBorder ? (
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}
              aria-hidden
            >
              <path
                d="M1 99 L1 1 L99 1 L99 99 L1 99"
                fill="none"
                stroke="rgba(255,255,255,0.98)"
                strokeWidth="1.6"
                pathLength="1"
                strokeDasharray="1"
                strokeDashoffset="1"
                style={{ animation: 'dse-terminal-shell-border-draw 500ms linear forwards' }}
              />
            </svg>
          ) : null}

          {panelEraseBorder ? (
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}
              aria-hidden
            >
              <path
                d="M1 99 L1 1 L99 1 L99 99 L1 99"
                fill="none"
                stroke="rgba(255,255,255,0.98)"
                strokeWidth="1.6"
                pathLength="1"
                strokeDasharray="1"
                strokeDashoffset="0"
                style={{ animation: 'dse-terminal-shell-border-erase 500ms linear forwards' }}
              />
            </svg>
          ) : null}

          <div style={{ height: '100%', width: '100%', opacity: panelHideContent ? 0 : 1, transition: 'opacity 500ms ease', position: 'relative', zIndex: 1 }}>
            <div
              style={{
                width: `${contentInverseScale * 100}%`,
                height: `${contentInverseScale * 100}%`,
                transform: `scale(${contentScale})`,
                transformOrigin: 'top left',
                transition: `transform ${Math.max(0, Number(expandSettleMs) || EXPAND_SETTLE_MS_DEFAULT)}ms ease`,
              }}
            >
              {children({
                isExpanded,
                toggleExpand,
                requestClose,
                phase,
                contentScale,
                contentInverseScale,
              })}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
